import { Form, FormInstance, Input, Spin } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { ColorSelect, FormItem, message } from 'components';
import SelectCustomerFullTextSearch from 'components/customers/SelectCustomerFullTextSearch';
import { validateMessages, vipCustomersMessages } from 'messages';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useCreateVipCustomerMutation, useGetVipCustomerDetailQuery, useUpdateVipCustomerMutation } from 'services';
import {
  useCreateCustomerGroupMutation,
  useGetCustomerGroupDetailQuery,
  useUpdateCustomerGroupMutation
} from 'services/customer-groups';
import { CreateCustomerGroupDto, CreateVipCustomerDto } from 'types';
import { createVipCustomerInitialValues, updateVipCustomerInitialValues, vipCustomersValidationRules } from 'utils';

export type CustomerGroupFromProps = {
  onChangeLoading?: (value: boolean) => void;
  onCreateSuccess?: () => void;
  customerGroupId?: number;
};

export type CustomerGroupFromRefProps = {
  form: FormInstance<VipCustomerFormType>;
  isLoading: boolean;
};

export type VipCustomerFormType = Omit<CreateCustomerGroupDto, 'parentId'> & {};
const VipCustomerForm = forwardRef<CustomerGroupFromRefProps, CustomerGroupFromProps>(
  ({ onChangeLoading, onCreateSuccess, customerGroupId }, ref) => {
    useImperativeHandle(ref, () => ({
      form: form,
      isLoading: isLoadingCreate || isLoadingUpdate
    }));

    const { data: vipCustomer, isLoading: isLoadingDetail } = useGetCustomerGroupDetailQuery(customerGroupId!, {
      skip: !customerGroupId,
      refetchOnMountOrArgChange: true
    });

    useEffect(() => {
      if (vipCustomer && customerGroupId) {
        form.setFieldsValue({
          ...vipCustomer.data
        });
      }
    }, [vipCustomer, customerGroupId]);

    const [form] = Form.useForm<VipCustomerFormType>();

    const [onCreate, { isLoading: isLoadingCreate }] = useCreateCustomerGroupMutation();
    const [onUpdate, { isLoading: isLoadingUpdate }] = useUpdateCustomerGroupMutation();

    const onFinish = ({ ...values }: VipCustomerFormType) => {
      const data: CreateCustomerGroupDto = {
        ...values
      };
      if (!customerGroupId) {
        onCreate(data)
          .unwrap()
          .then((rs) => {
            message.systemSuccess(rs.message);
            onCreateSuccess?.();
          });
      } else {
        onUpdate({
          customerGroupId,
          ...data
        })
          .unwrap()
          .then((rs) => {
            message.systemSuccess(rs.message);
            onCreateSuccess?.();
          });
      }
    };
    useEffect(() => {
      if (onChangeLoading) {
        onChangeLoading(isLoadingCreate || isLoadingUpdate);
      }
    }, [onChangeLoading, isLoadingCreate, isLoadingUpdate]);

    const handleSelectCustomer = (value: DefaultOptionType) => {
      form.setFieldsValue({
        name: value.customerName
      });
    };

    return (
      <Form
        scrollToFirstError={{ behavior: 'smooth', block: 'start' }}
        labelAlign='right'
        labelCol={{
          flex: '180px'
        }}
        requiredMark={false}
        form={form}
        name='vipCustomerForm'
        onFinish={onFinish}
        layout='horizontal'
        validateMessages={validateMessages}
        initialValues={customerGroupId ? updateVipCustomerInitialValues : createVipCustomerInitialValues}
      >
        <Spin spinning={isLoadingCreate || isLoadingDetail || isLoadingUpdate}>
          <FormItem.FloatLabel<VipCustomerFormType>
            name='name'
            label={vipCustomersMessages.groupName}
            rules={vipCustomersValidationRules.name}
          >
            <Input />
          </FormItem.FloatLabel>

          <Form.Item<VipCustomerFormType> label={vipCustomersMessages.color} name='color'>
            <ColorSelect />
          </Form.Item>
        </Spin>
      </Form>
    );
  }
);
export default VipCustomerForm;
